import { useState, useEffect } from 'react';
import { propertyAdminService, userService } from 'services/api';
import SearchableSelect from './SearchableSelect';
import PropertyCombobox from './PropertyCombobox';
import PasswordInput from './PasswordInput';
import ConfirmActionModal from './ConfirmActionModal';
import styles from './PropertyAdminModal.module.css';

const emptyForm = () => ({ nombre: '', apellido: '', email: '', telefono: '', password: '', property_id: '' });

/**
 * PropertyAdminModal
 * Props:
 *   onClose()              — cerrar modal
 *   onSuccess(message)     — callback al guardar correctamente
 *   fixedProperty          — { id, name } si viene desde Propiedades (bloquea el select de propiedad)
 */
export default function PropertyAdminModal({ onClose, onSuccess, fixedProperty = null }) {
  const [tab,        setTab]        = useState('assign'); // 'assign' | 'create'
  const [admins,     setAdmins]     = useState([]);
  const [propertyAdmins, setPropertyAdmins] = useState([]); // F-02: para saber si la propiedad ya tiene otro admin
  const [selAdmin,   setSelAdmin]   = useState('');
  const [form,       setForm]       = useState(() => ({
    ...emptyForm(),
    property_id: fixedProperty?.id || '',
  }));
  const [alert,   setAlert]   = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [confirmReplace, setConfirmReplace] = useState(null); // { adminActual, run } | null

  useEffect(() => {
    // Cargar solo los admins (filtro server-side, ver PERF-04 — antes traía
    // los ~9,750 usuarios del sistema para quedarse con un puñado) y los ya
    // asignados para excluirlos.
    Promise.all([
      userService.getAll({ role: 'admin', limit: 200 }).catch(() => ({ data: { data: [] } })),
      propertyAdminService.getAll().catch(() => ({ data: { property_admins: [] } })),
    ]).then(([usersRes, adminsRes]) => {
      const allAdmins = usersRes.data?.data || usersRes.data || [];
      const allPropertyAdmins = adminsRes.data?.property_admins || [];
      setPropertyAdmins(allPropertyAdmins);

      // IDs de admins ya asignados a la propiedad fija (si aplica)
      const assignedIds = new Set(
        allPropertyAdmins
          .filter(pa => fixedProperty ? String(pa.property_id) === String(fixedProperty.id) : false)
          .map(pa => pa.user_id)
      );

      setAdmins(allAdmins.filter(u => !assignedIds.has(u.id)));
    });

  }, [fixedProperty]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // F-02: una propiedad tiene un solo admin a la vez — si ya tiene uno
  // DISTINTO al que se está por asignar/crear, hay que avisar antes de
  // reemplazarlo (el backend ya lo reemplaza correctamente en los 3 caminos,
  // esto es solo el aviso en pantalla). `excludeUserId` es el admin que se
  // está asignando (para no "avisar" de sí mismo); en "Crear nuevo" siempre
  // es null porque el usuario todavía no existe.
  const adminActualDe = (propertyId, excludeUserId = null) => {
    const pa = propertyAdmins.find(pa =>
      String(pa.property_id) === String(propertyId) &&
      (excludeUserId === null || String(pa.user_id) !== String(excludeUserId))
    );
    return pa?.user ? `${pa.user.name} ${pa.user.last_name || ''}`.trim() : null;
  };

  // Tab: asignar admin existente
  const doAssign = async () => {
    setSaving(true);
    try {
      await propertyAdminService.assign({ user_id: selAdmin, property_id: form.property_id });
      onSuccess('Administrador asignado correctamente');
      onClose();
    } catch (err) {
      setAlert({ type: 'danger', message: err.response?.data?.message || 'Error al asignar' });
    } finally { setSaving(false); setConfirmReplace(null); }
  };

  const handleAssign = () => {
    if (!selAdmin)          return setAlert({ type: 'danger', message: 'Selecciona un administrador' });
    if (!form.property_id) return setAlert({ type: 'danger', message: 'Selecciona una propiedad' });
    const adminActual = adminActualDe(form.property_id, selAdmin);
    if (adminActual) return setConfirmReplace({ adminActual, run: doAssign });
    doAssign();
  };

  // Tab: crear nuevo admin
  const doCreate = async () => {
    setSaving(true);
    try {
      const res = await propertyAdminService.create(form);
      // correo_bienvenida: true = se envió el acceso; false = la cuenta quedó creada pero el correo falló; sin valor = la cuenta ya existía
      const envio = res.data?.correo_bienvenida;
      onSuccess(envio === true
        ? 'Administrador creado y asignado correctamente. Se envió un correo con su acceso.'
        : envio === false
          ? 'Administrador creado y asignado correctamente, pero NO se pudo enviar el correo con su acceso: entrégale la contraseña por otro medio.'
          : 'Administrador creado y asignado correctamente');
      onClose();
    } catch (err) {
      setAlert({ type: 'danger', message: err.response?.data?.message || 'Error al crear' });
    } finally { setSaving(false); setConfirmReplace(null); }
  };

  const handleCreate = () => {
    if (!form.nombre || !form.email || !form.password)
      return setAlert({ type: 'danger', message: 'Nombre, email y contraseña son obligatorios' });
    // Mismas reglas que el backend (validarPassword): mínimo 8 y no solo espacios.
    if (form.password.length < 8)
      return setAlert({ type: 'danger', message: 'La contraseña debe tener al menos 8 caracteres' });
    if (!form.password.trim())
      return setAlert({ type: 'danger', message: 'La contraseña no puede ser solo espacios' });
    if (!form.property_id)
      return setAlert({ type: 'danger', message: 'Selecciona una propiedad' });
    const adminActual = adminActualDe(form.property_id);
    if (adminActual) return setConfirmReplace({ adminActual, run: doCreate });
    doCreate();
  };

  const propName = fixedProperty?.name || null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal ${styles.modalAdminPropiedad}`} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="modal-header">
          <div>
            <h3><i className={`fas fa-user-tie ${styles.iconoTitulo}`} />
              Administrador de propiedad
            </h3>
            {propName && (
              <div className={styles.propiedad}>
                <i className={`fas fa-building ${styles.iconoPropiedad}`} />{propName}
              </div>
            )}
          </div>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>

        {/* Tabs */}
        <div className={styles.pestanas}>
          {[
            { key: 'assign', label: 'Admin existente', icon: 'fa-user-check' },
            { key: 'create', label: 'Crear nuevo',     icon: 'fa-user-plus'  },
          ].map(t => (
            <button key={t.key} onClick={() => { setTab(t.key); setAlert(null); }}
              className={tab === t.key ? styles.pestanaActiva : styles.pestana}>
              <i className={`fas ${t.icon} ${styles.iconoPestana}`} />{t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="modal-body">
          {alert && <div className={`alert alert-${alert.type}`}>{alert.message}</div>}

          {/* Selector de propiedad (solo si no viene fija) */}
          {!fixedProperty && (
            <div className="form-group">
              <label className="form-label">Propiedad *</label>
              <PropertyCombobox value={form.property_id} placeholder="Seleccione una propiedad..." allowEmpty={false} minWidth={0} maxWidth={9999}
                onChange={(id) => set('property_id', id)} />
            </div>
          )}

          {tab === 'assign' ? (
            /* ── Asignar admin existente ── */
            <div className="form-group">
              <label className="form-label">Administrador</label>
              {admins.length === 0 ? (
                <div className={styles.sinAdmins}>
                  <i className={`fas fa-info-circle ${styles.iconoInfo}`} />
                  No hay administradores disponibles. Usa la pestaña <strong>Crear nuevo</strong>.
                </div>
              ) : (
                <SearchableSelect value={selAdmin} onChange={(id) => setSelAdmin(id)} placeholder="Seleccione un administrador..." allowEmpty={false} minWidth={0} maxWidth={9999} ariaLabel="Administrador"
                  options={admins.map(a => ({ value: a.id, label: `${a.name} ${a.last_name || ''}`.trim(), sub: a.email }))} />
              )}
            </div>
          ) : (
            /* ── Crear nuevo admin ── */
            <>
              <div className={styles.rejilla}>
                <div className="form-group">
                  <label className="form-label">Nombre *</label>
                  <input className="form-control" value={form.nombre} onChange={e => set('nombre', e.target.value)} placeholder="Nombre" />
                </div>
                <div className="form-group">
                  <label className="form-label">Apellido</label>
                  <input className="form-control" value={form.apellido} onChange={e => set('apellido', e.target.value)} placeholder="Apellido" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Email *</label>
                <input type="email" className="form-control" value={form.email} onChange={e => set('email', e.target.value)} placeholder="correo@ejemplo.com" />
              </div>
              <div className={styles.rejilla}>
                <div className="form-group">
                  <label className="form-label">Teléfono</label>
                  <input className="form-control" value={form.telefono} onChange={e => set('telefono', e.target.value)} placeholder="3001234567" />
                </div>
                <div className="form-group">
                  <label className="form-label">Contraseña *</label>
                  <PasswordInput value={form.password} onChange={e => set('password', e.target.value)} placeholder="Mín. 8 caracteres" autoComplete="new-password" />
                </div>
              </div>
              <div className={styles.aviso}>
                <i className={`fas fa-info-circle ${styles.iconoInfo}`} />
                Se creará con rol <strong>admin</strong>
                {propName ? <> y se asignará a <strong>{propName}</strong></> : ' y se asignará a la propiedad seleccionada'}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" disabled={saving}
            onClick={tab === 'assign' ? handleAssign : handleCreate}>
            {saving
              ? <><div className={`spinner ${styles.spinnerGuardando}`} /> Guardando...</>
              : tab === 'assign'
              ? <><i className="fas fa-user-check" /> Asignar</>
              : <><i className="fas fa-user-plus" /> Crear y asignar</>
            }
          </button>
        </div>
      </div>

      {confirmReplace && (
        <ConfirmActionModal
          title="Reemplazar administrador"
          confirmLabel="Sí, reemplazar"
          confirmIcon="fa-user-check"
          loading={saving}
          onClose={() => setConfirmReplace(null)}
          onConfirm={confirmReplace.run}
        >
          Esta propiedad ya tiene un administrador asignado (<strong>{confirmReplace.adminActual}</strong>).
          Al continuar, perderá el acceso a esta propiedad — sus otras propiedades (si administra más de una) no se ven afectadas.
        </ConfirmActionModal>
      )}
    </div>
  );
}